import { TypedDocumentNode, gql } from '@apollo/client';

import { RangeInput, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { sortBy, uniqueBy } from '@sorare/core/src/lib/arrays';
import { withFragments } from '@sorare/core/src/lib/gql';

import type { useComposeBuy_anyCardInterface } from './__generated__/index.graphql';

const isTypeNumber = (value: unknown) => typeof value === 'number';

type Props<T> = {
  composeBuyCards: T[];
  filters: {
    showAffordableOnly?: boolean;
    priceRange: Nullable<RangeInput>;
    averageScoreRange?: Nullable<RangeInput>;
  };
  remainingPoints?: number;
};

export const useComposeBuy = withFragments(
  <T extends useComposeBuy_anyCardInterface>({
    composeBuyCards,
    filters,
    remainingPoints = 0,
  }: Props<T>) => {
    const { getUserFiatAmountInMajorUnit, toMonetaryAmount } =
      useMonetaryAmount();

    const composeBuyCardsSortedByPrice = sortBy(
      anyCard => anyCard.livePrimaryOffer?.price?.usdCents || 0,
      composeBuyCards.slice()
    );
    const cheapestComposeBuyCard = composeBuyCardsSortedByPrice[0];
    const cheapestComposeBuyPrice = getUserFiatAmountInMajorUnit(
      toMonetaryAmount(cheapestComposeBuyCard?.livePrimaryOffer?.price)
    );

    const moreExpensiveComposeBuyCard =
      composeBuyCardsSortedByPrice[composeBuyCardsSortedByPrice.length - 1];
    const moreExpensiveComposeBuyPrice = getUserFiatAmountInMajorUnit(
      toMonetaryAmount(moreExpensiveComposeBuyCard?.livePrimaryOffer?.price)
    );

    const highlightedComposeBuyCards = uniqueBy(
      card => card.slug,
      [
        moreExpensiveComposeBuyCard,
        composeBuyCardsSortedByPrice[
          Math.floor(composeBuyCardsSortedByPrice.length / 2)
        ],
        cheapestComposeBuyCard,
      ].filter(Boolean)
    );

    const filteredComposeBuyCards = composeBuyCards.filter(anyCard => {
      const price = getUserFiatAmountInMajorUnit(
        toMonetaryAmount(anyCard?.livePrimaryOffer?.price)
      );

      const averageScore = anyCard?.lastTenPlayedSo5AverageScore;
      if (anyCard?.sport === Sport.NBA) {
        if (
          isTypeNumber(averageScore) &&
          filters.showAffordableOnly &&
          averageScore > remainingPoints
        ) {
          return false;
        }
        if (
          !filters.showAffordableOnly &&
          isTypeNumber(filters?.averageScoreRange?.min) &&
          isTypeNumber(filters.averageScoreRange?.max) &&
          isTypeNumber(averageScore) &&
          (averageScore < filters.averageScoreRange.min ||
            averageScore > filters.averageScoreRange.max)
        ) {
          return false;
        }
      }

      return isTypeNumber(filters.priceRange?.min) &&
        isTypeNumber(filters.priceRange?.max)
        ? filters.priceRange.min < price && filters.priceRange.max > price
        : true;
    });

    return {
      composeBuyPriceRange: {
        min: cheapestComposeBuyPrice,
        max: moreExpensiveComposeBuyPrice,
      },
      highlightedComposeBuyCards,
      filteredComposeBuyCards,
    };
  },
  {
    anyCardInterface: gql`
      fragment useComposeBuy_anyCardInterface on AnyCardInterface {
        slug
        anyPlayer {
          slug
        }
        sport
        lastTenPlayedSo5AverageScore: averageScore(
          type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
        )
        livePrimaryOffer {
          id
          price {
            ...useMonetaryAmount_monetaryAmount
          }
        }
      }
      ${useMonetaryAmount.fragments.monetaryAmount}
    ` as TypedDocumentNode<useComposeBuy_anyCardInterface>,
  }
);
